/**
 * The colours a field option may be given.
 *
 * ── Why a list rather than a colour picker ─────────────────────────────────
 *
 * These are TOKENS, not hex. --brand-blue is re-themed per theme, so a chip
 * that stored `#1450 8c` would be the one thing on the board that ignored dark
 * mode. project_field_options.color_key has carried the token since
 * 20261029000100 and its check constraint is the real vocabulary; everything
 * here is the browser's half of that same list.
 *
 * ── Why five and not seven ─────────────────────────────────────────────────
 *
 * The constraint allows seven: the five below plus 'review' and 'revision'.
 * Those two are not colours, they are lifecycle names project_statuses uses,
 * and the stylesheet renders them as aliases — 'review' is neutral, 'revision'
 * is danger. Offering them here would put two pairs of identical swatches in
 * the picker under names that suggest they differ, and somebody choosing
 * "Revision" for a Platform option would be picking red by a name that has
 * nothing to do with their board.
 *
 * They stay legal in the database on purpose. A field seeded by a template can
 * hold one, and a picker that refused to display a value the database allows
 * would be worse than one that simply does not offer it — see colorLabel.
 */

export interface FieldColor {
  /** The token, as stored. */
  key: string;
  /** What a person picking it should read. */
  label: string;
}

export const FIELD_COLORS: FieldColor[] = [
  { key: 'neutral', label: 'Grey' },
  { key: 'brand', label: 'Blue' },
  { key: 'success', label: 'Green' },
  { key: 'warning', label: 'Amber' },
  { key: 'danger', label: 'Red' },
];

/** The colour a new option gets when nobody chooses one. */
export const DEFAULT_FIELD_COLOR = 'neutral';

/**
 * What to call a stored colour, including one this picker does not offer.
 *
 * A template-seeded option seeded as 'review' has to render as something, and
 * the honest something is its own name rather than a silent fallback to Grey —
 * which would tell a manager their option is grey right up until they look at
 * a card and find it is not.
 */
export function colorLabel(key: string): string {
  return FIELD_COLORS.find((color) => color.key === key)?.label ?? key;
}

/**
 * The options a `<select>` should show for an option that currently holds
 * `current`.
 *
 * Ordinarily just FIELD_COLORS. When the option holds a token outside the
 * offered five, that token is prepended so the control has a value to display:
 * a select whose own value is missing from its options renders blank in some
 * browsers, which reads as the colour having been lost.
 */
export function colorChoices(current: string): FieldColor[] {
  if (FIELD_COLORS.some((color) => color.key === current)) {
    return FIELD_COLORS;
  }
  return [{ key: current, label: colorLabel(current) }, ...FIELD_COLORS];
}
